import React, { useState, useEffect } from 'react';
import { FaPencilAlt, FaBan, FaCheck } from 'react-icons/fa';
import { api } from '../utils/api';

function Modal({ title, onClose, children, wide }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={wide ? { width: 640, maxWidth: '95vw' } : {}}>
        <h3>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Field({ label, children, mb = 12 }) {
  return (
    <div className="form-group" style={{ marginBottom: mb }}>
      <label>{label}</label>
      {children}
    </div>
  );
}

export default function Admin() {
  const [tab,           setTab]           = useState('instructors');
  const [instructors,   setInstructors]   = useState([]);
  const [students,      setStudents]      = useState([]);
  const [trainingTypes, setTrainingTypes] = useState([]);
  const [users,         setUsers]         = useState([]);
  const [modal,         setModal]         = useState(null);
  const [form,          setForm]          = useState({});
  const [error,         setError]         = useState('');
  const [busy,          setBusy]          = useState(false);

  useEffect(() => { reload(); }, []);

  async function reload() {
    api.instructors.listAll().then(setInstructors).catch(() => {});
    api.students.listAll().then(setStudents).catch(() => {});
    api.trainingTypes.listAll().then(setTrainingTypes).catch(() => {});
    api.users.list().then(setUsers).catch(() => {});
  }

  function set(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function openAdd(type)         { setForm({});         setError(''); setModal({ type, mode: 'add' }); }
  function openEdit(type, record){ setForm({ ...record }); setError(''); setModal({ type, mode: 'edit', record }); }

  // ── Save helpers ─────────────────────────────────────────────────────────────

  async function saveInstructor() {
    if (!form.name?.trim())          { setError('Name is required.'); return; }
    if (!form.instructor_id?.trim()) { setError('Instructor ID is required.'); return; }
    if (modal.mode === 'add' && form.createLogin) {
      if (!form.email?.trim())    { setError('Email is required to create a login account.'); return; }
      if (!form.tempPassword?.trim()) { setError('Temporary password is required to create a login account.'); return; }
    }
    setBusy(true); setError('');
    try {
      if (modal.mode === 'add') {
        await api.instructors.create(form);
        if (form.createLogin) {
          await api.users.create({
            name:     form.name,
            email:    form.email,
            password: form.tempPassword,
            role:     'instructor',
          });
        }
      } else {
        await api.instructors.update(modal.record.id, { ...form, active: modal.record.active });
      }
      await api.instructors.listAll().then(setInstructors);
      if (form.createLogin) await api.users.list().then(setUsers);
      setModal(null);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function saveStudent() {
    if (!form.name?.trim())       { setError('Name is required.'); return; }
    if (!form.student_id?.trim()) { setError('Student ID is required.'); return; }
    setBusy(true); setError('');
    try {
      if (modal.mode === 'add') {
        await api.students.create(form);
      } else {
        await api.students.update(modal.record.id, { ...form, active: modal.record.active });
      }
      await api.students.listAll().then(setStudents);
      setModal(null);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function saveTrainingType() {
    if (!form.name?.trim()) { setError('Name is required.'); return; }
    setBusy(true); setError('');
    try {
      if (modal.mode === 'add') {
        await api.trainingTypes.create({
          name: form.name,
          prefill_notes: form.prefill_notes || null,
          sort_order: Number(form.sort_order) || 100
        });
      } else {
        await api.trainingTypes.update(modal.record.id, {
          name: form.name,
          prefill_notes: form.prefill_notes || null,
          sort_order: Number(form.sort_order) || modal.record.sort_order,
          active: modal.record.active
        });
      }
      await api.trainingTypes.listAll().then(setTrainingTypes);
      setModal(null);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function saveUser() {
    setBusy(true); setError('');
    try {
      await api.users.create(form);
      await api.users.list().then(setUsers);
      setModal(null);
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  // ── Toggle active ─────────────────────────────────────────────────────────────

  async function toggleActive(type, record) {
    const next = record.active ? 0 : 1;
    if (!window.confirm(`${next ? 'Reactivate' : 'Deactivate'} "${record.name}"?`)) return;
    try {
      if (type === 'instructor') {
        await api.instructors.update(record.id, { ...record, active: next });
        await api.instructors.listAll().then(setInstructors);
      } else if (type === 'student') {
        await api.students.update(record.id, { ...record, active: next });
        await api.students.listAll().then(setStudents);
      } else {
        await api.trainingTypes.update(record.id, { ...record, active: next });
        await api.trainingTypes.listAll().then(setTrainingTypes);
      }
    } catch {}
  }

  // ── Styles ────────────────────────────────────────────────────────────────────

  const tabStyle = t => ({
    padding: '8px 18px', border: 'none', cursor: 'pointer', fontWeight: 600,
    background: tab === t ? '#1A2E5A' : '#e8edf5',
    color: tab === t ? 'white' : '#1A2E5A',
    borderRadius: '5px 5px 0 0', fontSize: 13
  });

  // ── Derived lists ─────────────────────────────────────────────────────────────

  const activeInstructors   = instructors.filter(i => i.active);
  const inactiveInstructors = instructors.filter(i => !i.active);
  const activeStudents      = students.filter(s => s.active);
  const inactiveStudents    = students.filter(s => !s.active);
  const activeTypes         = trainingTypes.filter(t => t.active);
  const inactiveTypes       = trainingTypes.filter(t => !t.active);

  // ── Row components ────────────────────────────────────────────────────────────

  function InstructorRow({ i }) {
    return (
      <tr style={!i.active ? { opacity: 0.5, background: '#f9f9f9' } : {}}>
        <td style={{ fontWeight: 600 }}>
          {i.name}
          {!i.active && <span style={{ fontSize: 11, color: '#999', marginLeft: 6 }}>(inactive)</span>}
        </td>
        <td>{i.instructor_id}</td>
        <td>{i.email || '—'}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-secondary btn-icon" title="Edit" onClick={() => openEdit('instructor', i)}>
            <FaPencilAlt />
          </button>
          <button
            className={`btn btn-icon ${i.active ? 'btn-danger' : 'btn-success'}`}
            style={{ marginLeft: 4 }}
            title={i.active ? 'Deactivate' : 'Reactivate'}
            onClick={() => toggleActive('instructor', i)}
          >
            {i.active ? <FaBan /> : <FaCheck />}
          </button>
        </td>
      </tr>
    );
  }

  function StudentRow({ s }) {
    return (
      <tr style={!s.active ? { opacity: 0.5, background: '#f9f9f9' } : {}}>
        <td style={{ fontWeight: 600 }}>
          {s.name}
          {!s.active && <span style={{ fontSize: 11, color: '#999', marginLeft: 6 }}>(inactive)</span>}
        </td>
        <td>{s.student_id}</td>
        <td>{s.email || '—'}</td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-secondary btn-icon" title="Edit" onClick={() => openEdit('student', s)}>
            <FaPencilAlt />
          </button>
          <button
            className={`btn btn-icon ${s.active ? 'btn-danger' : 'btn-success'}`}
            style={{ marginLeft: 4 }}
            title={s.active ? 'Deactivate' : 'Reactivate'}
            onClick={() => toggleActive('student', s)}
          >
            {s.active ? <FaBan /> : <FaCheck />}
          </button>
        </td>
      </tr>
    );
  }

  function TrainingTypeRow({ t }) {
    const hasPrefill = !!(t.prefill_notes);
    return (
      <tr style={!t.active ? { opacity: 0.5, background: '#f9f9f9' } : {}}>
        <td style={{ fontWeight: 600, width: 40, color: '#888', textAlign: 'center' }}>{t.sort_order}</td>
        <td style={{ fontWeight: 600 }}>
          {t.name}
          {!t.active && <span style={{ fontSize: 11, color: '#999', marginLeft: 6 }}>(inactive)</span>}
        </td>
        <td>
          {hasPrefill
            ? <span style={{ fontSize: 12, color: '#1a7a4a', fontWeight: 600 }}>Yes</span>
            : <span style={{ fontSize: 12, color: '#aaa' }}>—</span>
          }
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          <button className="btn btn-secondary btn-icon" title="Edit" onClick={() => openEdit('trainingType', t)}>
            <FaPencilAlt />
          </button>
          <button
            className={`btn btn-icon ${t.active ? 'btn-danger' : 'btn-success'}`}
            style={{ marginLeft: 4 }}
            title={t.active ? 'Deactivate' : 'Reactivate'}
            onClick={() => toggleActive('trainingType', t)}
          >
            {t.active ? <FaBan /> : <FaCheck />}
          </button>
        </td>
      </tr>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div>
      <div className="section-title">Admin</div>

      <div style={{ display: 'flex', gap: 4, marginBottom: 0 }}>
        <button style={tabStyle('instructors')}   onClick={() => setTab('instructors')}>Instructors</button>
        <button style={tabStyle('students')}       onClick={() => setTab('students')}>Students</button>
        <button style={tabStyle('trainingTypes')}  onClick={() => setTab('trainingTypes')}>Training Types</button>
        <button style={tabStyle('users')}          onClick={() => setTab('users')}>App Users</button>
      </div>

      <div className="card" style={{ borderRadius: '0 8px 8px 8px' }}>

        {/* ── Instructors ── */}
        {tab === 'instructors' && (
          <>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Instructors ({activeInstructors.length} active{inactiveInstructors.length > 0 ? `, ${inactiveInstructors.length} inactive` : ''})</h2>
              <button className="btn btn-success btn-sm" onClick={() => openAdd('instructor')}>+ Add Instructor</button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Instructor ID</th><th>Email</th><th>Actions</th></tr></thead>
                  <tbody>
                    {[...activeInstructors, ...inactiveInstructors].map(i => <InstructorRow key={i.id} i={i} />)}
                    {instructors.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#aaa', padding: 24 }}>No instructors yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Students ── */}
        {tab === 'students' && (
          <>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Students ({activeStudents.length} active{inactiveStudents.length > 0 ? `, ${inactiveStudents.length} inactive` : ''})</h2>
              <button className="btn btn-success btn-sm" onClick={() => openAdd('student')}>+ Add Student</button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Student ID</th><th>Email</th><th>Actions</th></tr></thead>
                  <tbody>
                    {[...activeStudents, ...inactiveStudents].map(s => <StudentRow key={s.id} s={s} />)}
                    {students.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#aaa', padding: 24 }}>No students yet</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── Training Types ── */}
        {tab === 'trainingTypes' && (
          <>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>Training Types ({activeTypes.length} active{inactiveTypes.length > 0 ? `, ${inactiveTypes.length} inactive` : ''})</h2>
              <button className="btn btn-success btn-sm" onClick={() => openAdd('trainingType')}>+ Add Type</button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Order</th>
                      <th>Name</th>
                      <th>Pre-fill Notes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...activeTypes, ...inactiveTypes].map(t => <TrainingTypeRow key={t.id} t={t} />)}
                    {trainingTypes.length === 0 && (
                      <tr><td colSpan={4} style={{ textAlign: 'center', color: '#aaa', padding: 24 }}>No training types</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        {/* ── App Users ── */}
        {tab === 'users' && (
          <>
            <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2>App Users ({users.length})</h2>
              <button className="btn btn-success btn-sm" onClick={() => openAdd('user')}>+ Add User</button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <div className="table-wrap">
                <table>
                  <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Created</th></tr></thead>
                  <tbody>
                    {users.map(u => (
                      <tr key={u.id}>
                        <td>{u.name}</td>
                        <td>{u.email}</td>
                        <td><span className="badge badge-type">{u.role}</span></td>
                        <td style={{ fontSize: 12 }}>{u.created_at?.split('T')[0]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Instructor modal ── */}
      {modal?.type === 'instructor' && (
        <Modal title={modal.mode === 'add' ? 'Add Instructor' : 'Edit Instructor'} onClose={() => setModal(null)}>
          {error && <div className="alert alert-error">{error}</div>}
          <Field label="Full Name *">
            <input type="text" value={form.name || ''} onChange={e => set('name', e.target.value)} autoFocus />
          </Field>
          <Field label="Instructor ID *">
            <input type="text" value={form.instructor_id || ''} onChange={e => set('instructor_id', e.target.value)} placeholder="e.g. JS001" />
          </Field>
          <Field label="Email" mb={modal.mode === 'add' ? 16 : 20}>
            <input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} />
          </Field>

          {/* Create login account — only when adding a new instructor */}
          {modal.mode === 'add' && (
            <>
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0 16px' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 16, textTransform: 'none', fontSize: 14, fontWeight: 500, color: 'var(--text)' }}>
                <input
                  type="checkbox"
                  checked={!!form.createLogin}
                  onChange={e => set('createLogin', e.target.checked)}
                  style={{ width: 'auto', height: 'auto', margin: 0 }}
                />
                Also create an app login account for this instructor
              </label>

              {form.createLogin && (
                <div style={{ background: 'var(--light-gray)', border: '1px solid var(--border)', borderRadius: 6, padding: '14px 16px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                    Login Account Details
                  </div>
                  <Field label="Login Email * (must match email above or enter one now)">
                    <input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} placeholder="instructor@example.com" />
                  </Field>
                  <Field label="Temporary Password *" mb={4}>
                    <input type="text" value={form.tempPassword || ''} onChange={e => set('tempPassword', e.target.value)} placeholder="Instructor will be asked to change this" />
                  </Field>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Share this temporary password with the instructor. They can change it after logging in via <strong>Account → Change Password</strong>.
                  </div>
                </div>
              )}
            </>
          )}

          <div className="btn-row">
            <button className="btn btn-primary" onClick={saveInstructor} disabled={busy}>
              {busy ? 'Saving...' : modal.mode === 'add' ? 'Add Instructor' : 'Save Changes'}
            </button>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ── Student modal ── */}
      {modal?.type === 'student' && (
        <Modal title={modal.mode === 'add' ? 'Add Student' : 'Edit Student'} onClose={() => setModal(null)}>
          {error && <div className="alert alert-error">{error}</div>}
          <Field label="Full Name *">
            <input type="text" value={form.name || ''} onChange={e => set('name', e.target.value)} autoFocus />
          </Field>
          <Field label="Student ID *">
            <input type="text" value={form.student_id || ''} onChange={e => set('student_id', e.target.value)} placeholder="e.g. SG001" />
          </Field>
          <Field label="Email" mb={20}>
            <input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} />
          </Field>
          <div className="btn-row">
            <button className="btn btn-primary" onClick={saveStudent} disabled={busy}>
              {busy ? 'Saving...' : modal.mode === 'add' ? 'Add Student' : 'Save Changes'}
            </button>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ── Training Type modal ── */}
      {modal?.type === 'trainingType' && (
        <Modal
          title={modal.mode === 'add' ? 'Add Training Type' : `Edit — ${modal.record.name}`}
          onClose={() => setModal(null)}
          wide
        >
          {error && <div className="alert alert-error">{error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px', gap: 12, marginBottom: 12 }}>
            <Field label="Name *" mb={0}>
              <input type="text" value={form.name || ''} onChange={e => set('name', e.target.value)} autoFocus />
            </Field>
            <Field label="Order" mb={0}>
              <input
                type="number" min="1" step="10"
                value={form.sort_order ?? ''}
                onChange={e => set('sort_order', e.target.value)}
              />
            </Field>
          </div>
          <Field label="Pre-fill Session Notes (shown when this type is selected and notes are empty)" mb={20}>
            <textarea
              rows={16}
              value={form.prefill_notes || ''}
              onChange={e => set('prefill_notes', e.target.value)}
              placeholder="Leave blank for no pre-fill..."
              style={{ fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5 }}
            />
          </Field>
          <div className="btn-row">
            <button className="btn btn-primary" onClick={saveTrainingType} disabled={busy}>
              {busy ? 'Saving...' : modal.mode === 'add' ? 'Add Type' : 'Save Changes'}
            </button>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}

      {/* ── User modal ── */}
      {modal?.type === 'user' && (
        <Modal title="Add App User" onClose={() => setModal(null)}>
          {error && <div className="alert alert-error">{error}</div>}
          <Field label="Full Name *">
            <input type="text" value={form.name || ''} onChange={e => set('name', e.target.value)} autoFocus />
          </Field>
          <Field label="Email *">
            <input type="email" value={form.email || ''} onChange={e => set('email', e.target.value)} />
          </Field>
          <Field label="Password *">
            <input type="password" value={form.password || ''} onChange={e => set('password', e.target.value)} />
          </Field>
          <Field label="Role" mb={20}>
            <select value={form.role || 'instructor'} onChange={e => set('role', e.target.value)}>
              <option value="instructor">Instructor</option>
              <option value="admin">Admin</option>
            </select>
          </Field>
          <div className="btn-row">
            <button className="btn btn-primary" onClick={saveUser} disabled={busy}>
              {busy ? 'Saving...' : 'Add User'}
            </button>
            <button className="btn btn-secondary" onClick={() => setModal(null)}>Cancel</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
